import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import React from 'react';

import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import Detail, {
  NewsDetailProps,
} from '@/components/sections/news-activities/detail';
import { NewsRelatedProps } from '@/components/sections/news-activities/related';
import { removeHTMLTag } from '@/components/shared/utility/format';
import { MENU_LCV_ABOUT_NEWS } from '@/contents/lcv/link';
import {
  getLCVNewsActive,
  getLCVNewsByURL,
  getLCVNewsReleated,
} from '@/services/contentstack';

const NewsRelated = dynamic(
  () => import('@/components/sections/news-activities/related'),
  {
    ssr: false,
  }
);

const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);
export async function getStaticPaths() {
  const res = await getLCVNewsActive();

  const entries = res.data.entries;
  const paths = entries.map((entry) => ({
    params: {
      url: entry.url,
      category: entry.category[0].url,
    },
  }));
  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const res = await getLCVNewsByURL(params.url);
  const resRelated = await getLCVNewsReleated(params.url, params.category);

  res.data.entries = res.data.entries.map((o) => ({
    metatag: {
      title: o?.metatag?.title,
      description: o?.metatag?.description,
      img: {
        url: o?.metatag?.img?.url,
      },
    },
    category: o.category.map((c) => ({
      name: c?.name,
      url: c?.url,
    })),
    begin_date: o?.begin_date,
    name: o?.name,
    excerpt: o?.excerpt,
    image_thumb: {
      url: o?.image_thumb?.url,
      dimension: {
        width: o?.image_thumb?.dimension?.width,
        height: o?.image_thumb?.dimension?.height,
      },
    },
    image_detail: {
      url: o?.image_detail?.url,
      dimension: {
        width: o?.image_detail?.dimension?.width,
        height: o?.image_detail?.dimension?.height,
      },
    },
    detail_items: o?.detail_items
      ? o.detail_items.map((c) => ({
          detail_item: {
            detail_line: c?.detail_item?.detail_line,
            detail_title: c?.detail_item?.detail_title,
            detail_description: c?.detail_item?.detail_description,
            detail_image: c?.detail_item?.detail_image
              ? c?.detail_item?.detail_image.map((d) => ({
                  url: d?.url,
                  dimension: {
                    width: d?.dimension?.width,
                    height: d?.dimension?.height,
                  },
                }))
              : [],
            detail_image_layout: c?.detail_item?.detail_image_layout,
          },
        }))
      : [],
    gallery_title: o?.gallery_title,
    gallery: o?.gallery
      ? o?.gallery.map((c) => ({
          url: c?.url,
          title: c?.title,
          dimension: {
            width: c?.dimension?.width,
            height: c?.dimension?.height,
          },
        }))
      : [],
    youtube_id: o?.youtube_id,
    buttons: o?.buttons
      ? o?.buttons.map((c) => ({
          button: { ...c.button },
        }))
      : [],
  }));

  const entry = res.data.entries[0];

  if (!entry) {
    return {
      redirect: {
        destination: `${MENU_LCV_ABOUT_NEWS.url}/${params.category}`,
      },
    };
  }

  resRelated.data.entries = resRelated.data.entries.map((o) => ({
    id: o?.id,
    begin_date: o?.begin_date,
    name: o?.name,
    excerpt: o?.excerpt,
    image_thumb: {
      url: o?.image_thumb?.url,
    },
    category: o.category.map((c) => ({
      name: c?.name,
      url: c?.url,
    })),
    url: o?.url,
  }));

  return {
    props: JSON.parse(
      JSON.stringify({
        entry: entry || null,
        relatedEntries: resRelated.data.entries,
      })
    ),
  };
}

export default function Page(props) {
  const { entry, relatedEntries } = props;

  const metatag = {
    title: entry?.metatag?.title ? entry?.metatag?.title : entry?.name,
    description: entry?.metatag?.description
      ? entry?.metatag?.description
      : removeHTMLTag(entry?.excerpt),
    imgURL:
      entry?.metatag?.img && entry?.metatag?.img?.url
        ? entry?.metatag.img?.url
        : entry?.image_thumb?.url,
  };

  const data: NewsDetailProps = {
    category: {
      name: entry?.category?.[0]?.name,
      url: entry?.category?.[0]?.url,
    },
    imageDetail: {
      src: entry?.image_detail?.url,
      alt: entry?.name,
      width: entry?.image_detail?.dimension?.width,
      height: entry?.image_detail?.dimension.height,
    },
    title: entry?.name,
    beginDate: entry?.begin_date,
    excerpt: entry?.excerpt,
    detail_items: entry?.detail_items
      ? entry?.detail_items.map((o) => ({
          detail_line: o?.detail_item?.detail_line,
          detail_title: o?.detail_item?.detail_title,
          detail_description: o?.detail_item?.detail_description,
          detail_image: o?.detail_item?.detail_image.map((c) => ({
            src: c?.url,
            alt: entry?.name,
            width: c?.dimension?.width,
            height: c?.dimension?.height,
          })),
          detail_image_layout: o?.detail_item?.detail_image_layout,
        }))
      : [],
    youtubeId: entry?.youtube_id,
    galleryTitle: entry?.gallery_title,
    gallery: entry?.gallery
      ? entry?.gallery.map((o) => ({
          src: o?.url,
          alt: o?.title,
          width: o?.dimension?.width,
          height: o?.dimension?.height,
        }))
      : [],
    buttons: entry?.buttons ? entry?.buttons.map((o) => ({ ...o.button })) : [],
    backButton: {
      label: 'กลับไปข่าวสารและกิจกรรมทั้งหมด',
      url: MENU_LCV_ABOUT_NEWS.url,
      target: MENU_LCV_ABOUT_NEWS.target ?? undefined,
      icon: 'arrow-back',
      type: 'border',
    },
  };

  const relatedData: NewsRelatedProps = {
    title: 'ข่าวสารและกิจกรรมที่เกี่ยวข้อง',
    items: relatedEntries?.map((o) => {
      return {
        id: o?.id,
        category: {
          name: o?.category?.[0]?.name,
          url: o?.category?.[0]?.url,
        },
        beginDate: o?.begin_date,
        image: {
          src: o?.image_thumb ? o?.image_thumb?.url : '',
          alt: o?.name,
          width: o?.image_thumb?.dimension?.width,
          height: o?.image_thumb?.dimension?.height,
        },
        url: `${MENU_LCV_ABOUT_NEWS.url}/${o.category[0].url}/${o.url}`,
        title: o?.name,
        excerpt: o?.excerpt,
        button: {
          url: `${MENU_LCV_ABOUT_NEWS.url}/${o.category[0].url}/${o.url}`,
          label: 'ดูรายละเอียด',
          icon: 'arrow-forward',
        },
      };
    }),
  };

  return (
    <>
      <CustomMetatag {...metatag} />
      <main>
        <Detail {...data} />
        {!!relatedData.items && relatedData.items.length > 0 && (
          <NewsRelated {...relatedData} />
        )}
      </main>
      <FooterLCVGroup />
    </>
  );
}

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
